import React, { useEffect, useState } from 'react';
import { toast } from 'sonner';
import { FileText, Gauge, Coins, CheckCircle, X, Loader2, ShieldCheck } from 'lucide-react';

const API = `${process.env.REACT_APP_BACKEND_URL}/api`;

const TESIX_PLANS = [
  {
    id: 'basico',
    name: 'Tesix Básico',
    price: 129,
    pages: 'Hasta 80 páginas/mes',
    extra: 'Página adicional: $2.00',
    target: 'Freelancers, autónomos y profesionales independientes',
    popular: false,
  },
  {
    id: 'estandar',
    name: 'Tesix Estándar',
    price: 169,
    pages: '80 – 100 páginas/mes',
    extra: 'Página adicional: $1.50',
    target: 'Pymes y agencias que producen con regularidad',
    popular: false,
  },
  {
    id: 'profesional',
    name: 'Tesix Profesional',
    price: 189,
    pages: '100 – 120 páginas/mes',
    extra: 'Página adicional: $1.00',
    target: 'Empresas medianas con volumen constante',
    popular: true,
  },
  {
    id: 'enterprise',
    name: 'Tesix Enterprise',
    price: 249,
    pages: '120 – 150+ páginas/mes',
    extra: 'Volumen alto y condiciones negociables',
    target: 'Grandes empresas · custom agents · SLA 99.99%',
    popular: false,
  },
];

const SHARED_FEATURES = [
  'Contador de páginas en tiempo real en tu panel',
  '14 días de prueba en todos los planes',
  'Aynara + Memoria Operativa 369 incluidas',
  '20% de descuento en planes anuales',
];

export const TesixPlansSection = () => {
  const [selectedPlan, setSelectedPlan] = useState(null);
  const [form, setForm] = useState({ full_name: '', email: '', company: '', billing_cycle: 'monthly' });
  const [submitting, setSubmitting] = useState(false);
  const [paymentBanner, setPaymentBanner] = useState(null);

  useEffect(() => {
    const params = new URLSearchParams(window.location.search);
    const sessionId = params.get('tesix_session_id');
    if (params.get('tesix_cancelado')) {
      setPaymentBanner({ type: 'cancel', message: 'Pago cancelado. Puedes intentarlo de nuevo cuando quieras.' });
      return;
    }
    if (!sessionId) return;
    let attempts = 0;
    const poll = async () => {
      attempts += 1;
      try {
        const response = await fetch(`${API}/tesix/checkout/status/${sessionId}`);
        const data = await response.json();
        if (response.ok && data.status === 'complete') {
          setPaymentBanner({ type: 'success', message: `¡Suscripción ${data.plan_name} activada! Tus 14 días de prueba han comenzado. Te hemos registrado con ${data.email}.` });
          return;
        }
        if (attempts < 5) setTimeout(poll, 2000);
        else setPaymentBanner({ type: 'pending', message: 'Estamos confirmando tu pago con Stripe. Recibirás confirmación por email.' });
      } catch {
        if (attempts < 5) setTimeout(poll, 2000);
      }
    };
    poll();
  }, []);

  const openCheckout = (plan) => {
    setSelectedPlan(plan);
    setForm({ full_name: '', email: '', company: '', billing_cycle: 'monthly' });
  };

  const submitCheckout = async (event) => {
    event.preventDefault();
    setSubmitting(true);
    try {
      const response = await fetch(`${API}/tesix/checkout`, {
        method: 'POST',
        headers: { 'Content-Type': 'application/json' },
        body: JSON.stringify({ plan_id: selectedPlan.id, ...form }),
      });
      const data = await response.json();
      if (!response.ok) throw new Error(data.detail || 'No se pudo iniciar el pago');
      window.location.href = data.url;
    } catch (error) {
      toast.error(error.message || 'No se pudo iniciar el pago');
      setSubmitting(false);
    }
  };

  const annualPrice = (price) => Math.round(price * 12 * 0.8);

  return (
    <section className="py-golden-xl" id="tesix-plans-section" data-testid="tesix-plans-section">
      <div className="max-w-7xl mx-auto px-golden-md lg:px-golden-lg">
        {paymentBanner && (
          <div
            className={`mb-8 rounded-2xl border p-5 text-sm ${paymentBanner.type === 'success' ? 'border-emerald-500/40 bg-emerald-500/10 text-emerald-300' : paymentBanner.type === 'cancel' ? 'border-amber-500/40 bg-amber-500/10 text-amber-300' : 'border-sky-500/40 bg-sky-500/10 text-sky-300'}`}
            data-testid={`tesix-payment-${paymentBanner.type}`}
          >
            {paymentBanner.message}
          </div>
        )}
        <div className="mb-golden-lg text-center">
          <p className="text-[10px] uppercase tracking-[0.18em] text-text-muted">Nueva modalidad · Tesix</p>
          <h2 className="mt-3 text-3xl text-text-heading" data-testid="tesix-plans-heading">Tesix: paga solo por lo que tus agentes generan</h2>
          <p className="mt-3 text-text-body max-w-3xl mx-auto">
            1 página = 1 documento completo generado por los agentes (informe, propuesta, contenido, análisis o código).
            Un modelo de precio justo, medible y transparente, con contador en tiempo real.
          </p>
        </div>

        <div className="grid gap-5 md:grid-cols-2 xl:grid-cols-4">
          {TESIX_PLANS.map((plan) => (
            <div
              key={plan.id}
              className={`glass-card relative flex flex-col p-6 ${plan.popular ? 'border-primary/60 ring-1 ring-primary/40' : ''}`}
              data-testid={`tesix-plan-${plan.id}`}
            >
              {plan.popular && (
                <span className="absolute -top-3 left-1/2 -translate-x-1/2 rounded-full bg-primary px-4 py-1 text-[10px] font-bold uppercase tracking-wide text-white">Más popular</span>
              )}
              <div className="flex items-center gap-2">
                <FileText size={16} className="text-primary" />
                <p className="text-sm font-semibold text-text-heading">{plan.name}</p>
              </div>
              <p className="mt-4 text-4xl font-bold text-text-heading" data-testid={`tesix-plan-price-${plan.id}`}>
                ${plan.price}<span className="text-sm font-normal text-text-muted">/mes USD</span>
              </p>
              <p className="mt-3 text-sm font-semibold text-text-body">{plan.pages}</p>
              <p className="mt-1 text-xs text-text-muted">{plan.extra}</p>
              <p className="mt-4 flex-1 text-xs text-text-muted">{plan.target}</p>
              <button
                type="button"
                onClick={() => openCheckout(plan)}
                className={`mt-5 inline-flex justify-center whitespace-nowrap rounded-full px-4 py-3 text-sm font-semibold transition ${plan.popular ? 'btn-primary' : 'btn-secondary'}`}
                data-testid={`tesix-plan-cta-${plan.id}`}
              >
                Empezar prueba 14 días
              </button>
            </div>
          ))}
        </div>

        <div className="mt-8 grid gap-4 lg:grid-cols-[1.2fr_0.8fr]">
          <div className="glass-card p-6" data-testid="tesix-shared-features">
            <div className="flex items-center gap-2">
              <Gauge size={16} className="text-primary" />
              <p className="text-sm font-semibold text-text-heading">Incluido en todos los planes Tesix</p>
            </div>
            <ul className="mt-4 grid gap-2 sm:grid-cols-2">
              {SHARED_FEATURES.map((feature) => (
                <li key={feature} className="flex items-start gap-2 text-sm text-text-body">
                  <CheckCircle size={15} className="mt-0.5 shrink-0 text-primary" />
                  {feature}
                </li>
              ))}
            </ul>
          </div>
          <div className="glass-card p-6" data-testid="tesix-ncr-note">
            <div className="flex items-center gap-2">
              <Coins size={16} className="text-primary" />
              <p className="text-sm font-semibold text-text-heading">Créditos Neuralia para partners</p>
            </div>
            <p className="mt-3 text-sm text-text-body">
              Los partners y desarrolladores operan con créditos Neuralia: <span className="font-bold text-text-heading">1 crédito = $0.10 USD</span>, precio final todo incluido,
              transferibles, con recarga automática y alertas de consumo en tiempo real.
            </p>
          </div>
        </div>
      </div>

      {selectedPlan && (
        <div className="fixed inset-0 z-50 flex items-center justify-center bg-black/70 px-4 backdrop-blur-sm" data-testid="tesix-checkout-modal">
          <div className="w-full max-w-md rounded-3xl border border-border-subtle bg-slate-950 p-7 shadow-2xl">
            <div className="flex items-start justify-between">
              <div>
                <p className="text-[10px] uppercase tracking-[0.18em] text-text-muted">Activar {selectedPlan.name}</p>
                <p className="mt-1 text-2xl font-bold text-text-heading">
                  {form.billing_cycle === 'annual' ? `$${annualPrice(selectedPlan.price)}/año` : `$${selectedPlan.price}/mes`}
                </p>
                <p className="mt-1 text-xs text-emerald-400 font-semibold">14 días de prueba · no se cobra nada hoy</p>
              </div>
              <button type="button" onClick={() => setSelectedPlan(null)} className="rounded-full border border-border-subtle p-2 text-text-muted hover:text-text-heading" data-testid="tesix-checkout-close" aria-label="Cerrar">
                <X size={16} />
              </button>
            </div>
            <div className="mt-4 grid grid-cols-2 gap-2" data-testid="tesix-billing-toggle">
              <button type="button" onClick={() => setForm((prev) => ({ ...prev, billing_cycle: 'monthly' }))} className={`rounded-full px-4 py-2 text-sm font-semibold ${form.billing_cycle === 'monthly' ? 'bg-primary text-white' : 'border border-border-subtle text-text-muted'}`} data-testid="tesix-cycle-monthly">Mensual</button>
              <button type="button" onClick={() => setForm((prev) => ({ ...prev, billing_cycle: 'annual' }))} className={`rounded-full px-4 py-2 text-sm font-semibold ${form.billing_cycle === 'annual' ? 'bg-primary text-white' : 'border border-border-subtle text-text-muted'}`} data-testid="tesix-cycle-annual">Anual −20%</button>
            </div>
            <form onSubmit={submitCheckout} className="mt-4 space-y-3">
              <input
                className="w-full rounded-2xl border border-border-subtle bg-slate-900 px-4 py-3 text-sm text-text-heading"
                placeholder="Tu nombre completo"
                value={form.full_name}
                onChange={(event) => setForm((prev) => ({ ...prev, full_name: event.target.value }))}
                required
                minLength={2}
                data-testid="tesix-checkout-name"
              />
              <input
                type="email"
                className="w-full rounded-2xl border border-border-subtle bg-slate-900 px-4 py-3 text-sm text-text-heading"
                placeholder="Email de trabajo"
                value={form.email}
                onChange={(event) => setForm((prev) => ({ ...prev, email: event.target.value }))}
                required
                data-testid="tesix-checkout-email"
              />
              <input
                className="w-full rounded-2xl border border-border-subtle bg-slate-900 px-4 py-3 text-sm text-text-heading"
                placeholder="Empresa (opcional)"
                value={form.company}
                onChange={(event) => setForm((prev) => ({ ...prev, company: event.target.value }))}
                data-testid="tesix-checkout-company"
              />
              <button type="submit" disabled={submitting} className="btn-primary w-full justify-center rounded-full px-5 py-3 text-sm font-bold disabled:opacity-50" data-testid="tesix-checkout-submit">
                {submitting ? (<span className="inline-flex items-center gap-2"><Loader2 size={15} className="animate-spin" /> Abriendo pago seguro…</span>) : 'Continuar al pago con tarjeta'}
              </button>
              <p className="flex items-center justify-center gap-1.5 text-[11px] text-text-muted"><ShieldCheck size={13} /> Pago seguro procesado por Stripe · cancela cuando quieras</p>
            </form>
          </div>
        </div>
      )}
    </section>
  );
};

export default TesixPlansSection;
